import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { InvoiceRequestService } from './invoice-request.service';
import { InvoiceRequestStore } from './interfaces/api';

// The billing-request collector's store registry. It is the authority on which
// storeId values exist: a write naming an unregistered store is rejected with
// 422 STORE_UNKNOWN, so anything that creates a record has to check here first
// rather than trust a string in the app's own config.
//
// Cached for the session — the list changes only when a store is added or
// retired, and a lookup failure is treated as "nothing is registered" so a
// collector outage disables the feature instead of failing at save time.
@Injectable({
    providedIn: 'root',
})
export class StoreRegistryService {
    private api = inject(InvoiceRequestService);
    private stores$?: Observable<InvoiceRequestStore[]>;

    list(): Observable<InvoiceRequestStore[]> {
        if (!this.stores$) {
            this.stores$ = this.api.listStores().pipe(
                map((response) => response?.items ?? []),
                catchError(() => of([] as InvoiceRequestStore[])),
                shareReplay(1),
            );
        }
        return this.stores$;
    }

    // Ids a record may be written under. Retired stores are included: the
    // collector keeps them writable so in-flight orders can finish collecting.
    knownIds(): Observable<Set<string>> {
        return this.list().pipe(
            map((stores) => new Set(stores.map((store) => store.id))),
        );
    }
}
